import React from "react";
import { Box, Button, Checkbox, Flex, InlineNotification, Link, Text, useTranslation } from "@familyzone/component-library";
import { SectionHeading } from "../../shared/SectionHeading";
import { TabTypes } from ".";

interface TextSettingsTabProps {
  saving: boolean;
  disabled: boolean;
  onSave: (tabType: TabTypes) => void;

  textAnalysisEnabled: boolean;
  setTextAnalysisEnabled: (val: boolean) => void;
}

const TextSettingsTab = ({ saving, disabled, onSave, textAnalysisEnabled, setTextAnalysisEnabled }: TextSettingsTabProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" minH="100%">
      <Box backgroundColor="white" borderRadius="2xl" boxShadow="sm" pt="sp24">
        <Box px="sp24">
          <SectionHeading title={t("Real-time text analysis")} aria-level={2} />
          <Text color="neutrals.120" mt="sp8" ml="sp12" mr="sp12">
            {t("Proactively scans webpage text and blocks pages that contain content matching your configured filtering categories.")}
          </Text>
          <Box mt="sp16" color="neutrals.120">
            <InlineNotification
              notificationTitle="You must configure Content Filtering policy to use this feature."
              notificationDescription={
                <>
                  Real-time Text Analysis will only block websites that match categories you've set to block in Content Filtering.{" "}
                  <Link
                    textDecoration="underline"
                    href="https://help.linewize.com/hc/en-gb/articles/22788609666076"
                    isExternal={true}
                    style={{ color: "#156cb0" }}
                  >
                    Find out more on text analysis.
                  </Link>
                </>
              }
              status="warning"
            />
          </Box>
          <Box mt="sp24" ml="sp12" mr="sp12" color="neutrals.120">
            <Checkbox
              isChecked={textAnalysisEnabled}
              isDisabled={disabled}
              aria-label={`Turn on real-time text analysis ${textAnalysisEnabled ? "enabled" : "disabled"}`}
              onChange={(e) => setTextAnalysisEnabled(e.target.checked)}
            >
              <Text fontWeight="medium">{t("Turn on real-time text analysis")}</Text>
            </Checkbox>
          </Box>
        </Box>
        <Box bg="#F5FCFF" mt="sp24" px="sp24" py="sp24" borderBottomLeftRadius="2xl" borderBottomRightRadius="2xl">
          <Flex>
            <Button
              variant="primary"
              disabled={disabled}
              onClick={() => onSave(TabTypes.TEXT)}
              data-testid="save-text-content-aware-config-button"
            >
              {saving ? (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Saving...")}</div>
              ) : (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Save Changes")}</div>
              )}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default TextSettingsTab;
